import React, { useState } from "react";
import Box from "@amzn/meridian/box";
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import ButtonGroup, { ButtonOption } from "@amzn/meridian/button-group";
import Tooltip from "@amzn/meridian/tooltip";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import infoTokens from "@amzn/meridian-tokens/base/icon/info";
import { AvailableLabor, RosteredHeadcount } from "../../model";
import {
    TOOLTIP_AVAILABLE_LABOR_CARD,
    TOOLTIP_LABOR_SHARE,
    TOOLTIP_MISC_HOURS,
} from "../../../../i18n/tooltips";

type DisplayMode = "headcount" | "hours";

interface AvailableLaborDisplayProps {
    labor?: AvailableLabor | null;
    /**
     * @deprecated Legacy headcount data. Used as fallback to build AvailableLabor
     * when the backend hasn't migrated to the new availableLabor payload yet.
     * Only headcount values can be derived; hours will show as 0.
     */
    legacyHeadcount?: RosteredHeadcount | null;
}

/**
 * Formats a number for display. Values >= 1000 are shown with 'k' suffix
 * with one decimal precision (e.g. 38578 -> "38.6k").
 */
const formatValue = (value: number): string => {
    if (value >= 1000) {
        return `${(value / 1000).toFixed(1)}k`;
    }
    return String(value);
};

/**
 * Build AvailableLabor from legacy RosteredHeadcount.
 * Only headcount values are available from the old interface; hours default to 0.
 * Categories not present in the old interface (azReady*, laborShare, miscHours) default to 0.
 *
 * NOTE: The legacy `override` field from RosteredHeadcount is intentionally dropped here.
 * Override values are now managed through the TotalAttendance section, not the headcount
 * display. During migration, the backend should stop sending `override` in the headcount
 * object and instead include it in `totalAttendance.override`. See BACKEND_PAYLOAD_SPEC.md.
 */
const buildFromLegacyHeadcount = (hc: RosteredHeadcount): AvailableLabor => ({
    total: { headcount: hc.recommended, hours: 0 },
    vtoOpen: { headcount: hc.vtoOpen, hours: 0 },
    vtoAccepted: { headcount: hc.vtoAccepted, hours: 0 },
    vetOpen: { headcount: hc.vetOpen, hours: 0 },
    vetAccepted: { headcount: hc.vetAccepted, hours: 0 },
    azReadyOpen: { headcount: 0, hours: 0 },
    azReadyAccepted: { headcount: 0, hours: 0 },
    laborShare: { headcount: 0, hours: 0 },
    miscHours: { headcount: 0, hours: 0 },
});

interface LaborItem {
    key: keyof AvailableLabor;
    subLabel?: string;
}

interface LaborGroup {
    label: string;
    items: LaborItem[];
    tooltip?: string;
}

const LABOR_GROUPS: LaborGroup[] = [
    { label: "Total",       items: [{ key: "total" }] },
    { label: "VTO",         items: [{ key: "vtoOpen", subLabel: "Open" }, { key: "vtoAccepted", subLabel: "Accepted" }] },
    { label: "VET",         items: [{ key: "vetOpen", subLabel: "Open" }, { key: "vetAccepted", subLabel: "Accepted" }] },
    { label: "AZ Ready",    items: [{ key: "azReadyOpen", subLabel: "Open" }, { key: "azReadyAccepted", subLabel: "Accepted" }] },
    { label: "Labor Share", items: [{ key: "laborShare" }], tooltip: TOOLTIP_LABOR_SHARE },
    { label: "Misc Hours",  items: [{ key: "miscHours" }],  tooltip: TOOLTIP_MISC_HOURS },
];

const RosteredHeadcountDisplay: React.FC<AvailableLaborDisplayProps> = ({ labor, legacyHeadcount }) => {
    const [displayMode, setDisplayMode] = useState<DisplayMode>("headcount");

    // Prefer new availableLabor; fall back to building from deprecated headcount
    const data: AvailableLabor | null = labor ?? (legacyHeadcount ? buildFromLegacyHeadcount(legacyHeadcount) : null);

    const handleModeChange = (value: DisplayMode) => {
        setDisplayMode(value);
    };

    // Hours toggle is only meaningful when real availableLabor data is present
    const hasHoursData = !!labor;

    if (!data) {
        return (
            <Box type="outline" spacingInset="400">
                <Text type="h200">Available Labor</Text>
                <Text type="b200" color="secondary">No labor data available</Text>
            </Box>
        );
    }

    return (
        <Box type="outline" spacingInset="400">
            <Column spacing="500">
                <Row alignmentVertical="center" spacing="none" widths={["fill", "fit"]}>
                    <Row spacing="200" alignmentVertical="center">
                        <Text type="h200">Available Labor</Text>
                        <Tooltip
                            title={TOOLTIP_AVAILABLE_LABOR_CARD}
                            position="top"
                            maxWidth={280}
                        >
                            <Button type="icon" size="small" label="Info: Available Labor">
                                <Icon tokens={infoTokens} />
                            </Button>
                        </Tooltip>
                    </Row>
                    {hasHoursData && (
                        <ButtonGroup
                            value={displayMode}
                            onChange={handleModeChange}
                            size="small"
                            type="outline"
                            legend="Display mode"
                        >
                            <ButtonOption value="headcount" minWidth="72px">Headcount</ButtonOption>
                            <ButtonOption value="hours" minWidth="72px">Hours</ButtonOption>
                        </ButtonGroup>
                    )}
                </Row>

                {/* Grouped KPI row: alignmentVertical="bottom" so all value numbers sit on the same baseline */}
                <Row spacing="500" wrap="down" alignmentVertical="bottom">
                    {LABOR_GROUPS.map(({ label, items, tooltip }) => {
                        // For legacy data, skip groups where all items are zero (except Total)
                        const allZero = items.every(({ key }) =>
                            data[key].headcount === 0 && data[key].hours === 0
                        );
                        if (!hasHoursData && allZero && label !== "Total") {
                            return null;
                        }

                        const isMulti = items.length > 1;

                        return (
                            <Column key={label} spacing="200" alignmentHorizontal="center">
                                {/* Group parent label — always render the icon slot so every label
                                    row has identical height regardless of whether a tooltip exists. */}
                                <Row spacing="100" alignmentVertical="center" alignmentHorizontal="center">
                                    <Text type="b200" color="secondary" alignment="center">
                                        {label}
                                    </Text>
                                    {tooltip ? (
                                        <Tooltip
                                            title={tooltip}
                                            position="top"
                                            maxWidth={280}
                                        >
                                            <Button type="icon" size="small" label={`Info: ${label}`}>
                                                <Icon tokens={infoTokens} />
                                            </Button>
                                        </Tooltip>
                                    ) : (
                                        /* Invisible placeholder keeps all label rows the same height */
                                        <span style={{ display: 'inline-flex', width: 24, height: 24, visibility: 'hidden', flexShrink: 0 }} />
                                    )}
                                </Row>

                                {/* Sub-columns row — always rendered for consistent vertical height.
                                    Single items get an invisible placeholder sub-label so all
                                    groups' values sit at the same baseline. */}
                                <Row spacing="400" alignmentVertical="bottom">
                                    {items.map(({ key, subLabel }) => {
                                        const entry = data[key];
                                        const rawValue = displayMode === "headcount" ? entry.headcount : entry.hours;
                                        return (
                                            <Column key={key} spacing="100" alignmentHorizontal="center">
                                                {/* Always reserve a sub-label row; hide text for single items */}
                                                <Text
                                                    type="b100"
                                                    color="secondary"
                                                    alignment="center"
                                                >
                                                    {isMulti && subLabel ? subLabel : "\u00A0"}
                                                </Text>
                                                <Text type="h200" color="secondary" alignment="center">
                                                    {formatValue(rawValue)}
                                                </Text>
                                            </Column>
                                        );
                                    })}
                                </Row>
                            </Column>
                        );
                    })}
                </Row>
            </Column>
        </Box>
    );
};

export default RosteredHeadcountDisplay;
